

export const dynamic = "force-dynamic";

import { useState, useEffect, useMemo, useRef } from "react";
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Package, 
  Store
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi, vendorsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrency } from "@/lib/data";

// Types
interface Service {
  id: number;
  name: string;
  description?: string;
  selling_price: number;
  base_price: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
  image?: string;
  created_at?: string;
}

// Search Input Component
function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search services..."
        className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
      />
    </div>
  );
}

// Action Button
function ActionButton({ 
  onClick, 
  icon: Icon, 
  label, 
  variant = 'default' 
}: { 
  onClick: () => void; 
  icon: any; 
  label: string;
  variant?: 'default' | 'primary' | 'danger' | 'success';
}) {
  const colors = {
    default: 'text-stone-600 hover:bg-stone-100',
    primary: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-emerald-600 hover:bg-emerald-50',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Service Form Component
function ServiceForm({ 
  initialData, 
  onSubmit, 
  onCancel,
  vendors 
}: { 
  initialData?: Partial<Service>; 
  onSubmit: (data: any) => void;
  onCancel: () => void;
  vendors: { id: number; name: string }[];
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    base_price: String(initialData?.base_price || ""),
    selling_price: String(initialData?.selling_price || ""),
    vendor_id: String(initialData?.vendor_id || ""),
  });

  // Searchable dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter vendors based on search term
  const filteredVendors = useMemo(() => {
    if (!searchTerm) return vendors;
    return vendors.filter(v => v.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [vendors, searchTerm]);

  // Get selected vendor name
  const selectedVendor = vendors.find(v => String(v.id) === formData.vendor_id);

  const handleSelect = (vendorId: string) => {
    setFormData({ ...formData, vendor_id: vendorId });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      description: formData.description,
      base_price: parseFloat(formData.base_price) || 0,
      selling_price: parseFloat(formData.selling_price) || 0,
      vendor_id: formData.vendor_id ? parseInt(formData.vendor_id) : null,
    });
  };

  const commission = (parseFloat(formData.selling_price) || 0) - (parseFloat(formData.base_price) || 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Service Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter service name"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Service description"
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Base Price (Cost) *</label>
          <input
            type="number"
            value={formData.base_price}
            onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
            placeholder="0.00"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Selling Price *</label>
          <input
            type="number"
            value={formData.selling_price}
            onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
            placeholder="0.00"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            required
          />
        </div>
      </div>

      {commission > 0 && (
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
          <div className="text-xs text-stone-500">Your Commission</div>
          <div className="text-lg font-bold text-amber-700">{formatCurrency(commission)}</div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Vendor</label>
        <div ref={dropdownRef} className="relative">
          {/* Dropdown Trigger */}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none flex items-center justify-between text-left"
          >
            <span className={selectedVendor ? "text-stone-800" : "text-stone-400"}>
              {selectedVendor?.name || "No Vendor"}
            </span>
            <Search className="w-4 h-4 text-stone-400" />
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b border-stone-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search vendors..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                    autoFocus
                  />
                </div>
              </div>

              {/* Options List */}
              <div className="overflow-y-auto max-h-44">
                <button
                  type="button"
                  onClick={() => handleSelect("")}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-stone-50 transition-colors ${
                    formData.vendor_id === "" ? "bg-amber-50 text-amber-700" : "text-stone-700"
                  }`}
                >
                  No Vendor
                </button>
                {filteredVendors.map((vendor) => (
                  <button
                    key={vendor.id}
                    type="button"
                    onClick={() => handleSelect(String(vendor.id))}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-stone-50 transition-colors ${
                      formData.vendor_id === String(vendor.id) ? "bg-amber-50 text-amber-700" : "text-stone-700"
                    }`}
                  >
                    {vendor.name}
                  </button>
                ))}
                {filteredVendors.length === 0 && (
                  <div className="px-3 py-2 text-sm text-stone-400 text-center">
                    No vendors found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update" : "Add"} Service
        </button>
      </div>
    </form>
  );
}

export default function ServicesPage() {
  const { showToast } = useToast();
  
  const [services, setServices] = useState<Service[]>([]);
  const [vendors, setVendors] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  
  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingService, setDeletingService] = useState<Service | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesRes, vendorsRes] = await Promise.all([
        mastersApi.services.list(),
        vendorsApi.list(),
      ]);
      console.log('Services API response:', servicesRes);
      console.log('Services data:', servicesRes.data);
      const servicesData = servicesRes.data?.data || servicesRes.data || [];
      console.log('Extracted services:', servicesData);
      setServices(servicesData);
      setVendors(vendorsRes.data?.data || vendorsRes.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      showToast("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = useMemo(() => {
    if (!searchQuery) return services;
    const q = searchQuery.toLowerCase();
    return services.filter(s => 
      s.name.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q) ||
      s.vendor?.name.toLowerCase().includes(q)
    );
  }, [services, searchQuery]);

  const stats = useMemo(() => ({
    total: services.length,
    withVendor: services.filter(s => s.vendor_id).length,
    totalCommission: services.reduce((s, svc) => s + (svc.selling_price - svc.base_price), 0),
  }), [services]);

  const openDeleteModal = (service: Service) => {
    setDeletingService(service);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingService) return;
    
    setDeleteLoading(true);
    const previousServices = services;
    setServices(prev => prev.filter(s => s.id !== deletingService.id));
    
    try {
      await mastersApi.services.delete(deletingService.id);
      showToast("Service deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingService(null);
    } catch (error) {
      setServices(previousServices);
      showToast("Failed to delete service", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    // Close modal immediately for fast localhost experience
    setIsModalOpen(false);
    setEditingService(null);
    
    try {
      if (editingService) {
        const updated = { ...editingService, ...data };
        setServices(prev => prev.map(s => s.id === editingService.id ? updated as Service : s));
        await mastersApi.services.update(editingService.id, data);
        showToast("Service updated successfully", "success");
      } else {
        // Optimistic update - add placeholder immediately
        const tempId = Date.now();
        const optimisticService = {
          id: tempId,
          ...data,
          vendor: vendors.find(v => v.id === data.vendor_id)
        } as Service;
        setServices(prev => [optimisticService, ...prev]);
        
        const response = await mastersApi.services.create(data);
        const newService = response.data?.data || response.data;
        
        // Replace temp service with real one
        setServices(prev => prev.map(s => s.id === tempId ? newService : s));
        showToast("Service created successfully", "success");
      }
    } catch (error) {
      showToast("Failed to save service", "error");
      loadData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Services</h1>
        <p className="text-sm text-stone-500">Manage your services and pricing</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <SearchInput value={searchQuery} onChange={setSearchQuery} />
        </div>
        <button
          onClick={() => { setEditingService(null); setIsModalOpen(true); }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Service
        </button>
      </div>

      {/* Content */}
      {filteredServices.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No services found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Vendor</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Cost</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Sell</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Commission</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredServices.map((service) => (
                  <tr key={service.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-stone-800">{service.name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">{service.vendor?.name || '-'}</td>
                    <td className="px-4 py-3 text-right text-sm">{formatCurrency(service.base_price)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(service.selling_price)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-emerald-600">
                      {formatCurrency(service.selling_price - service.base_price)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <ActionButton onClick={() => { setEditingService(service); setIsModalOpen(true); }} icon={Pencil} label="" variant="default" />
                        <ActionButton onClick={() => openDeleteModal(service)} icon={Trash2} label="" variant="danger" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingService(null); }}
        title={editingService ? "Edit Service" : "Add New Service"}
        size="md"
      >
        <ServiceForm
          initialData={editingService || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingService(null); }}
          vendors={vendors}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingService(null); }}
        onConfirm={handleDelete}
        title="Delete Service"
        message="Are you sure you want to delete this service? This action cannot be undone."
        itemName={deletingService?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}
